import { cmsActiveTemplateType } from "@workspace/forms-spec";
import type { CmsTemplateEntityType } from "@workspace/api-client-react";

/**
 * CMS pages fall into three surfaces, all backed by the same `cms_pages` rows:
 *
 *  - Route-backed pages — authored as CMS pages but served by a dedicated route
 *    wrapper at a real path (e.g. slug "plans" renders at "/plans"). They are
 *    ALSO reachable through the generic "/p/:slug" harness, so that harness
 *    canonicalizes them to the real path to avoid duplicate content.
 *  - Entity templates — `templateEntityType` is set; they style the entity
 *    detail routes (FBO/airport/…) and have NO standalone public URL. The
 *    "/p/:slug" harness renders them against a representative sample entity for
 *    preview / inline editing only, so they are kept out of the index.
 *  - Ordinary one-off pages — plain content at "/p/:slug".
 *
 * This module is the single source of truth mapping a page to its public surface
 * so the renderer (canonical/noindex) and the admin Pages module agree.
 */

/**
 * Kept in lockstep with ROUTE_BACKED_CMS_SLUGS in
 * `artifacts/api-server/src/lib/sitemap-static-paths.ts` (which excludes these
 * slugs from the dynamic `/p/:slug` sitemap loop). Enforced by
 * `src/lib/seo/sitemap-lockstep.test.ts`.
 */
export const ROUTE_BACKED_SLUGS: Record<string, string> = {
  home: "/",
  plans: "/plans",
  advertise: "/advertise",
  "build-plan": "/build-plan",
  airports: "/airports",
  fbos: "/fbos",
  "flight-schools": "/flight-schools",
  services: "/services",
  maintenance: "/maintenance",
  experiences: "/experiences",
  hangar: "/hangar",
  map: "/map",
  compare: "/compare",
  "get-listed": "/get-listed",
  privacy: "/privacy",
  trust: "/trust",
  status: "/status",
};

const TEMPLATE_ENTITY_LABELS: Record<
  NonNullable<CmsTemplateEntityType>,
  string
> = {
  fbo: "FBO",
  airport: "Airport",
  flight_school: "Flight School",
  maintenance: "Maintenance",
  services: "Services",
  experience: "Experience",
};

/**
 * The real public path a route-backed CMS slug renders at, or null for an
 * ordinary page (which lives at "/p/:slug").
 */
export function cmsPageRealRoute(slug: string): string | null {
  return ROUTE_BACKED_SLUGS[slug] ?? null;
}

export type CmsPageRouteInfo = {
  /** Canonical public path, or null for an entity template (no standalone URL). */
  publicPath: string | null;
  /** Human-readable description of where this page surfaces. */
  surfaceLabel: string;
  /** True when the page is an entity template rather than a standalone page. */
  isTemplate: boolean;
};

/**
 * Resolve a CMS page's public surface from its slug + template type. Used by the
 * admin Pages list/editor to show the correct URL and label, and to distinguish
 * entity templates from ordinary pages.
 */
export function cmsPageRouteInfo(
  slug: string,
  templateEntityType: CmsTemplateEntityType | null | undefined,
): CmsPageRouteInfo {
  templateEntityType =
    templateEntityType ??
    (cmsActiveTemplateType(slug) as CmsTemplateEntityType);
  if (templateEntityType) {
    const label =
      TEMPLATE_ENTITY_LABELS[templateEntityType] ?? templateEntityType;
    const active = cmsActiveTemplateType(slug) === templateEntityType;
    return {
      publicPath: null,
      surfaceLabel: active
        ? `Styles ${label} Detail Pages`
        : `${label} Template Preview · not the active shared template`,
      isTemplate: true,
    };
  }
  const real = cmsPageRealRoute(slug);
  const path = real ?? `/p/${slug}`;
  return { publicPath: path, surfaceLabel: path, isTemplate: false };
}
